<script setup>
import { useShopStore } from '~/stores/shop'
import BasePageTitle from '~/components/BasePageTitle'
import ShopLister from '~/components/shop/ShopLister'

const { $label, $sentryCaptureException, $toast, payload } = useNuxtApp()
const route = useRoute()
const routeQuery = computed(() => route.query)
const fullPath = computed(() => route.fullPath)

useHead({
  title: `${$label('page.shop.title')} • Bookchoice`,
  meta: [
    {
      name: 'description',
      content: $label('page.shop.subTitle')
    }
  ],
  link: [
    {
      rel: 'canonical',
      href: 'https://www.bookchoice.com/shop'
    }
  ]
})

const shopStore = useShopStore()
const lister = ref(null)

const { data: genres, error: fetchGenresError } = await useAsyncData(
  'shop-genres',
  () => shopStore.getShopGenres(),
  {
    default: () => [],
    getCachedData: () => {
      return payload.data?.['shop-genres'] || payload.static?.['shop-genres']
    }
  }
)

if (fetchGenresError.value) {
  $sentryCaptureException(fetchGenresError.value)
}

function getParams() {
  const { sort, genre, page } = routeQuery.value
  const params = {
    pageSize: 24,
    pageIndex: page,
    genre
  }

  if (sort) {
    const [sortBy, sortDirection] = sort.split('-')
    params.sort = sortBy
    params.sortDirection = sortDirection
  }

  if (genre) {
    params.bookgenre = genres.value.find(
      (item) => item.id === decodeURIComponent(genre)
    )?.uid
  }

  return params
}

function scrollToTop() {
  try {
    if (window.scrollY > 0) lister.value.scrollIntoView({ behavior: 'smooth' })
    // eslint-disable-next-line no-empty
  } catch {}
}

const {
  data: booksData,
  error,
  status
} = await useAsyncData(
  fullPath.value,
  async () => {
    const params = getParams()

    setTimeout(scrollToTop, 100)

    const response = await shopStore.getShopEbooks(params)

    return {
      ...response,
      routeQuery: routeQuery.value
    }
  },
  {
    default: () => [],
    watch: [routeQuery]
  }
)

if (error.value) {
  $sentryCaptureException(error.value)
}

const pending = computed(() => status.value === 'pending')

onMounted(() => {
  if (error.value) {
    $toast.error($label('messages.notAbleToRetreiveBooks'))
  }
})
</script>

<template>
  <div>
    <BasePageTitle
      :header="$label('page.shop.title')"
      :subtitle="$label('page.shop.subTitle')"
    />

    <div ref="lister">
      <ShopLister
        :books="booksData.books"
        :genres="genres"
        :pagination="booksData.pagination"
        :is-loading="pending"
        :route-query="booksData.routeQuery"
      />
    </div>
  </div>
</template>
